import React from "react";
import { enUS as enLocale, es as esLocale } from "date-fns/locale";

import {
  LocalizationProvider,
  PickersLayout,
  PickersDay,
  PickersDayProps,
} from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import I24CalendarDatePicker from "assets/icons/I24CalendarDatePicker";

import { roundTime } from "utils/date";
import styled from "styled-components";
import { useTranslation } from "react-i18next";

type Props = {
  label?: string;
  required?: boolean;
  dateWithTimeMode?: boolean;
  minutesStep?: number;
  value?: Date;
  minDate?: Date;
  minDateMessage?: string;
  maxDate?: Date;
  maxDateMessage?: string;
  invalidDateMessage?: string;
  placeholder?: string;
  onChange: (date: Date | null) => void;
};

const StyledPickersLayout = styled(PickersLayout)({
  ".MuiDateCalendar-root .MuiPickersDay-root": {
    color: "rgba(26, 54, 53)",
    opacity: 1,
    fontWeight: 400,
  },
  ".MuiDateCalendar-root .MuiPickersDay-root.Mui-selected": {
    color: "rgba(256, 256, 256)",
  },
});

const StyledPickersDay = styled((props: PickersDayProps<Date>) => (
  <PickersDay {...props} />
))({
  "&.MuiPickersDay-root.MuiPickersDay-today.MuiPickersDay-hiddenDaySpacingFiller":
    {
      border: "none !important",
    },
  "&.MuiPickersDay-dayOutsideMonth": {
    color: "rgba(26, 54, 53, 0.4) !important",
  },
});

const DatePickerComponent: React.FC<Props> = ({
  dateWithTimeMode,
  value,
  onChange,
  minutesStep,
  ...props
}) => {
  const { i18n } = useTranslation();
  const locale = i18n.language?.startsWith("es") ? esLocale : enLocale;

  const onChangeDateTime = (date: Date | null) => {
    if (date) {
      const roundedDate = minutesStep ? roundTime(date, minutesStep) : date;
      onChange(roundedDate);
    } else {
      onChange(null);
    }
  };

  return (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      adapterLocale={{
        ...locale,
        options: { ...enLocale.options, weekStartsOn: 1 },
      }}
    >
      {dateWithTimeMode ? (
        <DatePicker
          {...props}
          value={value ? new Date(value) : null}
          onAccept={onChangeDateTime}
          format="MM/dd/yyyy hh:mm a"
          onChange={onChangeDateTime}
          slots={{
            layout: StyledPickersLayout,
            day: StyledPickersDay,
          }}
          slotProps={{
            textField: {
              variant: "outlined",
              fullWidth: true,
              InputLabelProps: {
                shrink: true,
              },
            },
          }}
        />
      ) : (
        <DatePicker
          {...props}
          showDaysOutsideCurrentMonth
          value={value ? new Date(value) : null}
          onAccept={onChange}
          format="MM/dd/yyyy"
          slots={{
            openPickerIcon: I24CalendarDatePicker,
            layout: StyledPickersLayout,
            day: StyledPickersDay,
          }}
          slotProps={{
            textField: {
              variant: "outlined",
              fullWidth: true,
              InputLabelProps: {
                shrink: true,
              },
            },
          }}
          onChange={onChange}
        />
      )}
    </LocalizationProvider>
  );
};

export default DatePickerComponent;
